/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useEffect, useRef } from 'react';

import { GenerationStatus } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';

import { getActivityInsights } from '../../../../api/activities/insights/getActivityInsights';
import { queryKey } from '../queryKey';
import {
  getPollingInterval,
  hasExceededMaxDuration,
  pendingInsightsAtom,
} from './pendingInsightsAtom';

/**
 * Shared ref to track which activity instances have shown toasts
 * This is outside the component so it persists across renders but can be cleared
 */
const shownToastsRef = new Set<number>();

/**
 * Clearing the toast tracking for a specific activity instance
 * Calling this when starting a new generation to allow toasts to show again
 */
export function clearToastTracking(activityInstanceId: number) {
  shownToastsRef.delete(activityInstanceId);
}

/**
 * Global hook that polls for pending insights across the entire app
 * Uses exponential backoff and stops after 5 minutes
 */
export function useGlobalInsightsPolling() {
  const queryClient = useQueryClient();
  const [pendingInsights, dispatch] = useAtom(pendingInsightsAtom);
  const timeoutsRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const clearActivityTimeout = (activityInstanceId: number) => {
    const timeout = timeoutsRef.current.get(activityInstanceId);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(activityInstanceId);
    }
  };

  const pollInsight = async (activityInstanceId: number) => {
    try {
      // Check if we've exceeded max duration before polling
      const insight = pendingInsights.find(
        (i) => i.activityInstanceId === activityInstanceId
      );

      if (!insight) {
        // Insight was removed, stop polling
        clearActivityTimeout(activityInstanceId);
        return;
      }

      if (hasExceededMaxDuration(insight)) {
        // Stop polling and remove from storage
        dispatch({ type: 'remove', activityInstanceId });
        clearActivityTimeout(activityInstanceId);

        // Show failure toast if we haven't already
        if (!shownToastsRef.has(activityInstanceId)) {
          toast.critical(translate('Unexpected Error'));
          shownToastsRef.add(activityInstanceId);
        }
        return;
      }

      // Update last polled timestamp
      dispatch({ type: 'updateLastPolled', activityInstanceId });

      // Fetch the insights using queryClient.fetchQuery to cache the result
      const activityInsights = await queryClient.fetchQuery({
        queryKey: queryKey.insightsById(activityInstanceId),
        queryFn: () => getActivityInsights(activityInstanceId),
      });

      if (!activityInsights) {
        // No insights yet, schedule next poll with exponential backoff
        const interval = getPollingInterval(insight);
        const timeout = setTimeout(
          () => pollInsight(activityInstanceId),
          interval
        );
        timeoutsRef.current.set(activityInstanceId, timeout);
        return;
      }

      const status = activityInsights.generationStatus;

      if (status === GenerationStatus.Completed) {
        // Success! Show toast and remove from pending
        dispatch({ type: 'remove', activityInstanceId });
        clearActivityTimeout(activityInstanceId);

        // No need to invalidate - fetchQuery already cached the result
        // Show success toast with navigation link (only once)
        if (!shownToastsRef.has(activityInstanceId)) {
          showSuccessToast(activityInstanceId);
          shownToastsRef.add(activityInstanceId);
        }
      } else if (status === GenerationStatus.Failed) {
        // Failed, remove from pending and show error
        dispatch({ type: 'remove', activityInstanceId });
        clearActivityTimeout(activityInstanceId);

        // No need to invalidate - fetchQuery already cached the result
        // Show failure toast (only once)
        if (!shownToastsRef.has(activityInstanceId)) {
          toast.critical(translate('Unexpected Error'));
          shownToastsRef.add(activityInstanceId);
        }
      } else if (
        status === GenerationStatus.Pending ||
        status === GenerationStatus.InsufficientData
      ) {
        // Still pending or insufficient data, continue polling with exponential backoff
        const interval = getPollingInterval(insight);
        const timeout = setTimeout(
          () => pollInsight(activityInstanceId),
          interval
        );
        timeoutsRef.current.set(activityInstanceId, timeout);
      }
    } catch (error) {
      console.error(
        `Error polling insights for activity ${activityInstanceId}:`,
        error
      );

      // Stop polling on error - don't continue retrying
      dispatch({ type: 'remove', activityInstanceId });
      clearActivityTimeout(activityInstanceId);

      // Show error toast if we haven't already
      if (!shownToastsRef.has(activityInstanceId)) {
        toast.critical(translate('Unexpected Error'));
        shownToastsRef.add(activityInstanceId);
      }
    }
  };

  const showSuccessToast = (activityInstanceId: number) => {
    const message = (toastInstance: any) => {
      const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        toast.dismiss(toastInstance.id);
        window.location.hash = `#results/${activityInstanceId}/showInsights=true`;
      };

      return (
        <div className="flex items-center gap-2">
          <span>{translate('Your AI Insights are ready.')}</span>
          <a
            href={`#results/${activityInstanceId}/showInsights=true`}
            onClick={handleClick}
            className="text-sm text-white leading-6 font-medium underline hover:no-underline cursor-pointer"
          >
            {translate('View Now')}
          </a>
        </div>
      );
    };

    toast.positive(message, {
      duration: Infinity,
      disableClose: false,
    });
  };

  useEffect(() => {
    // Start polling for all pending insights
    pendingInsights.forEach((insight) => {
      // Only start polling if we're not already polling this insight
      if (!timeoutsRef.current.has(insight.activityInstanceId)) {
        // Calculate initial delay based on when it was last polled
        const timeSinceLastPoll = Date.now() - insight.lastPolledAt;
        const interval = getPollingInterval(insight);
        const initialDelay = Math.max(0, interval - timeSinceLastPoll);

        const timeout = setTimeout(
          () => pollInsight(insight.activityInstanceId),
          initialDelay
        );
        timeoutsRef.current.set(insight.activityInstanceId, timeout);
      }
    });

    // Cleanup on unmount
    return () => {
      timeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      timeoutsRef.current.clear();
    };
  }, [pendingInsights]);

  return null;
}
